import { useState } from 'react'
import {
  AppWindow,
  BookText,
  BrainCircuit,
  Check,
  ChevronDown,
  Cpu,
  Download,
  Gauge,
  History,
  KeyRound,
  Lock,
  Mic,
  ShieldCheck,
  Sparkles,
  Wand2,
  Zap,
} from 'lucide-react'

import {
  getCheckoutProviderLabel,
  LAUNCH_CTA_LABEL,
  LAUNCH_CTA_URGENCY,
  LAUNCH_PRICE,
  RETAIL_PRICE,
} from '../lib/purchases'
import { BuyButton } from './BuyButton'
import { HeroDemo } from './HeroDemo'

const PORTAL_URL = (import.meta.env.VITE_APP_PORTAL_URL as string | undefined)
  ?? 'https://app.agentwhisper.altuzar.dev'

type Feature = {
  title: string
  description: string
  icon: React.ElementType
  pill?: string
  span?: string
  points?: string[]
}

const features: Feature[] = [
  {
    title: 'Local Whisper transcription',
    description:
      'Audio is transcribed on your Mac with whisper.cpp. It never touches a server. Pick the model that fits your machine, from Tiny to Large v3 Turbo.',
    icon: Cpu,
    pill: 'On-device',
    span: 'lg:col-span-2',
    points: [
      'Runs fully offline once a model is downloaded',
      '8+ Whisper models, so you choose accuracy or speed',
      'Optimized for Apple Silicon, works on Intel too',
    ],
  },
  {
    title: 'Works in every app',
    description:
      'Hit the shortcut, speak, and the words land at your cursor in Mail, Slack, Xcode, Notes, your terminal, or anywhere else you can type.',
    icon: AppWindow,
    points: ['System-wide hotkey', 'Types at the cursor, no copy-paste dance'],
  },
  {
    title: 'AI cleanup with your own keys',
    description:
      'Pipe raw transcripts through Claude, OpenAI, Gemini, Groq, Mistral, or a local Ollama model. Your key, your endpoint, your tokens.',
    icon: BrainCircuit,
    pill: 'BYOK',
    span: 'lg:row-span-2',
    points: [
      '15+ providers supported out of the box',
      'Fix grammar, drop filler words, match your style',
      'Fully optional. Local-only mode works great',
      'Pay providers cents directly, no middleman markup',
    ],
  },
  {
    title: 'Apple Intelligence compatible',
    description:
      'Works alongside Apple Intelligence today. When enhanced Apple Intelligence arrives in macOS 27 Golden Gate, support is included at no extra charge.',
    icon: Sparkles,
    pill: 'Included forever',
    points: [
      'Built for modern macOS text workflows',
      'Future Apple Intelligence updates included forever',
    ],
  },
  {
    title: 'Power Mode',
    description:
      'Context-aware prompt switching: different cleanup style per app or website. Casual in Slack, precise in your editor.',
    icon: Zap,
    pill: 'Pro workflow',
  },
  {
    title: 'Personal dictionary',
    description:
      'Teach it your names, acronyms, and jargon once, then stop fixing phonetically spelled "Kubernetes."',
    icon: BookText,
  },
  {
    title: 'Private by architecture',
    description:
      'No accounts in the app, no telemetry, no audio uploads. The only network calls are the ones you configure with your own API keys.',
    icon: ShieldCheck,
    pill: 'Zero telemetry',
    span: 'lg:col-span-2',
    points: [
      'Audio never leaves your Mac',
      'Honest trade-off: AI cleanup sends text (not audio) to the provider you choose',
    ],
  },
  {
    title: 'Menubar-first',
    description:
      'Lives quietly in your menu bar with no Dock icon. A full dashboard with history and stats when you want it.',
    icon: History,
  },
]

const steps = [
  {
    icon: Mic,
    title: 'Press the shortcut',
    description: 'One global hotkey from any app. The recorder pill appears instantly.',
  },
  {
    icon: Wand2,
    title: 'Speak naturally',
    description: 'Whisper transcribes locally; optional AI cleanup polishes grammar and filler words.',
  },
  {
    icon: Sparkles,
    title: 'Words land at your cursor',
    description: 'Your text is typed right where you were, formatted, clean, and ready to send.',
  },
]

const pricingIncludes = [
  'Lifetime license. Pay once, use forever',
  'All current and future updates included forever',
  'Apple Intelligence compatible, including enhanced Apple Intelligence in macOS 27 Golden Gate',
  'Local Whisper models, unlimited dictation',
  'AI cleanup with your own API keys (BYOK)',
  'Power Mode, personal dictionary, history dashboard',
  'Instant download after checkout',
]

const faqs = [
  {
    q: 'What does BYOK mean?',
    a: 'Bring Your Own Key. Transcription is free and local forever. If you want AI cleanup of your transcripts, you plug in your own API key (Claude, OpenAI, Gemini, Groq, Mistral…) or point it at a local Ollama model. You pay the provider directly, typically pennies, and we never see your text or charge a subscription for it.',
  },
  {
    q: 'Is it really a one-time purchase?',
    a: `Yes. ${RETAIL_PRICE} retail, ${LAUNCH_PRICE} during launch. No subscription, no seat licenses, no "pro tier" upsell. One payment, lifetime license, all updates included forever.`,
  },
  {
    q: 'Does it work with Apple Intelligence?',
    a: 'Yes. AgentWhisper is Apple Intelligence compatible today, and enhanced Apple Intelligence support for macOS 27 Golden Gate will be included without charge for lifetime customers.',
  },
  {
    q: 'Does my audio leave my Mac?',
    a: 'No. Transcription runs on-device with whisper.cpp. If you enable optional AI cleanup, only the transcribed text (never audio) goes to the provider you configured with your own key. Local-only mode makes zero network calls.',
  },
  {
    q: 'What are the system requirements?',
    a: 'macOS 14.4 or later. Apple Silicon is fastest, Intel is supported. Whisper models take roughly 100 MB to 3 GB of disk depending on which you choose.',
  },
  {
    q: 'What happens after I buy?',
    a: 'Checkout takes about a minute with card or Apple Pay, handled by RevenueCat + Stripe. Right after payment you create your account. Yes, after payment, because nobody likes signing up first. Your download is waiting for you.',
  },
  {
    q: 'How is this different from macOS built-in dictation?',
    a: 'Whisper-class accuracy (built-in dictation hasn’t meaningfully improved in years), works identically in every app, supports custom vocabulary, and can clean up your speech with AI. Plus a history dashboard so nothing you dictate is ever lost.',
  },
  {
    q: 'Can I use a discount code?',
    a: 'Yes. Launch pricing is applied automatically at checkout. Gift recipients can enter their one-time code in Stripe Checkout.',
  },
]

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="rounded-brand border border-white/10 bg-white/[0.03] transition-colors hover:border-white/20">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
        aria-expanded={open}
      >
        <span className="text-base font-semibold text-fg">{q}</span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-fg-muted transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && <p className="px-6 pb-5 text-sm leading-relaxed text-fg-dim">{a}</p>}
    </div>
  )
}

export function LandingPage() {
  // Marketing copy is intentionally independent of the active RevenueCat package.
  // This lets us run a private low-cost checkout verification without publishing
  // that test amount in the public launch pricing.
  const price = LAUNCH_PRICE
  const checkoutProvider = getCheckoutProviderLabel()

  return (
    <div className="min-h-screen overflow-x-hidden bg-ink font-sans text-fg">
      {/* Background glows */}
      <div className="pointer-events-none fixed inset-0 z-0">
        <div className="absolute -left-48 -top-48 h-[600px] w-[600px] rounded-full bg-brand-purple/35 blur-[100px]" />
        <div className="absolute -right-72 top-96 h-[700px] w-[700px] rounded-full bg-brand-pink/25 blur-[100px]" />
      </div>

      {/* Nav */}
      <nav className="relative z-10 mx-auto flex max-w-6xl items-center justify-between px-4 py-6 sm:px-6 lg:px-8">
        <a href="/" className="flex items-center gap-2.5">
          <img src="/app-icon.png" alt="AgentWhisper icon" className="h-9 w-9" />
          <span className="hidden text-lg font-bold tracking-tight sm:inline">AgentWhisper</span>
        </a>
        <div className="hidden items-center gap-8 text-sm font-medium text-fg-dim md:flex">
          <a href="#features" className="transition-colors hover:text-fg">Features</a>
          <a href="#how-it-works" className="transition-colors hover:text-fg">How it works</a>
          <a href="#pricing" className="transition-colors hover:text-fg">Pricing</a>
          <a href="#faq" className="transition-colors hover:text-fg">FAQ</a>
          <a href={PORTAL_URL} className="transition-colors hover:text-fg">Sign in</a>
        </div>
        <BuyButton size="md" label={`Buy, ${price}`} />
      </nav>

      <main className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* ============================== HERO ============================== */}
        <section className="grid items-center gap-14 py-12 lg:grid-cols-2 lg:py-20">
          <div className="space-y-8 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5">
              <Sparkles className="h-4 w-4 text-brand-pink" />
              <span className="text-xs font-semibold uppercase tracking-wider text-fg-dim">
                Launch sale: {LAUNCH_PRICE} today, normally {RETAIL_PRICE}
              </span>
            </div>

            <h1 className="text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
              Voice dictation that <span className="text-gradient">stays on your Mac</span>
            </h1>

            <p className="mx-auto max-w-xl text-lg leading-relaxed text-fg-dim lg:mx-0">
              Hit a shortcut, speak, and your words land at the cursor in any app. Whisper runs
              locally, your audio never leaves your machine, and it works alongside Apple
              Intelligence. Optional AI cleanup uses <em className="not-italic text-fg">your own API keys</em>.
            </p>

            <div className="flex flex-col items-center gap-4 sm:flex-row lg:justify-start">
              <BuyButton label={LAUNCH_CTA_LABEL} />
              <a
                href="#how-it-works"
                className="inline-flex items-center gap-2 rounded-brand border border-white/15 bg-white/5 px-6 py-4 text-base font-semibold text-fg-dim transition-colors hover:border-white/30 hover:text-fg"
              >
                See how it works
              </a>
            </div>

            <p className="text-sm font-semibold text-brand-pink">
              {LAUNCH_CTA_URGENCY}
            </p>

            <p className="text-xs font-medium uppercase tracking-wide text-fg-muted">
              One-time purchase · macOS 14.4+ · Secure checkout via {checkoutProvider}
            </p>
          </div>

          <HeroDemo />
        </section>

        {/* ====================== TRUST / PRIVACY STRIP ===================== */}
        <section className="mt-10 grid gap-4 rounded-brand-lg border border-white/10 bg-white/[0.03] p-6 sm:grid-cols-4">
          {[
            { icon: Lock, label: 'Audio never leaves your Mac' },
            { icon: KeyRound, label: 'Your AI keys, your data, your bill' },
            { icon: Sparkles, label: 'Apple Intelligence compatible' },
            { icon: Gauge, label: 'Zero telemetry, zero accounts in-app' },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center justify-center gap-3 text-sm font-medium text-fg-dim">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-gradient-soft">
                <Icon className="h-4 w-4 text-brand-pink" />
              </span>
              {label}
            </div>
          ))}
        </section>

        {/* ============================ FEATURES ============================ */}
        <section id="features" className="mt-28 scroll-mt-24 space-y-12">
          <div className="space-y-4 text-center">
            <h2 className="text-3xl font-bold lg:text-4xl">
              Everything Apple dictation <span className="text-gradient">should have been</span>
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-fg-dim">
              Whisper-class accuracy, AI-polished output, and total privacy for people who
              type for a living.
            </p>
          </div>

          <div className="grid auto-rows-[minmax(220px,auto)] grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
            {features.map((feature) => (
              <div
                key={feature.title}
                className={`group relative overflow-hidden rounded-brand-lg border border-white/10 bg-white/[0.03] transition-all duration-300 hover:border-white/25 hover:bg-white/[0.05] ${feature.span ?? ''}`}
              >
                <div className="flex h-full flex-col p-7">
                  <div className="mb-5 flex items-start justify-between">
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-gradient-soft ring-1 ring-white/10 transition-transform duration-300 group-hover:scale-110">
                      <feature.icon className="h-6 w-6 text-brand-pink" />
                    </span>
                    {feature.pill && (
                      <span className="rounded-full bg-brand-gradient px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                        {feature.pill}
                      </span>
                    )}
                  </div>
                  <h3 className="mb-2 text-xl font-bold">{feature.title}</h3>
                  <p className="text-sm leading-relaxed text-fg-dim">{feature.description}</p>
                  {feature.points && (
                    <ul className="mt-auto space-y-2 pt-5">
                      {feature.points.map((point) => (
                        <li key={point} className="flex items-start gap-2 text-xs font-medium text-fg-muted">
                          <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-pink" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* =========================== HOW IT WORKS ========================= */}
        <section id="how-it-works" className="mt-28 scroll-mt-24 space-y-12">
          <div className="space-y-4 text-center">
            <h2 className="text-3xl font-bold lg:text-4xl">Three seconds from thought to text</h2>
            <p className="mx-auto max-w-2xl text-lg text-fg-dim">
              No windows to open, no buttons to find. Dictation becomes muscle memory.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {steps.map((step, i) => (
              <div key={step.title} className="relative rounded-brand-lg border border-white/10 bg-white/[0.03] p-8">
                <span className="absolute -top-4 left-8 flex h-8 w-8 items-center justify-center rounded-full bg-brand-gradient text-sm font-bold text-white">
                  {i + 1}
                </span>
                <step.icon className="mb-4 h-7 w-7 text-brand-pink" />
                <h3 className="mb-2 text-lg font-bold">{step.title}</h3>
                <p className="text-sm leading-relaxed text-fg-dim">{step.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ============================= PRICING ============================ */}
        <section id="pricing" className="mt-28 scroll-mt-24">
          <div className="mx-auto max-w-3xl">
            <div className="space-y-4 text-center">
              <h2 className="text-3xl font-bold lg:text-4xl">
                Pay once. <span className="text-gradient">Dictate forever.</span>
              </h2>
              <p className="text-lg text-fg-dim">
                No subscription. Software you run on your own Mac shouldn’t rent itself back to
                you.
              </p>
            </div>

            <div className="relative mt-12 overflow-hidden rounded-brand-lg border border-brand-purple/40 bg-white/[0.04] shadow-2xl shadow-brand-purple/20">
              <div className="absolute inset-x-0 top-0 h-1 bg-brand-gradient" />
              <div className="grid gap-10 p-8 sm:p-12 lg:grid-cols-[1.2fr_1fr]">
                <div className="space-y-6">
                  <span className="inline-flex items-center gap-2 rounded-full bg-brand-gradient px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white">
                    <Sparkles className="h-3.5 w-3.5" /> Launch price, limited time
                  </span>
                  <div className="flex items-end gap-4">
                    <span className="text-6xl font-extrabold tracking-tight">{price}</span>
                    <span className="pb-2 text-2xl font-semibold text-fg-muted line-through">{RETAIL_PRICE}</span>
                  </div>
                  <p className="text-sm leading-relaxed text-fg-dim">
                    Lifetime license for the Mac app. AI cleanup is BYOK, so you pay providers
                    directly (typically cents per day), and we never charge a monthly fee.
                  </p>
                  <BuyButton label={`Buy AgentWhisper, ${price}`} />
                  <p className="text-xs text-fg-muted">
                    Card or Apple Pay · Checkout by {checkoutProvider} · Discount codes accepted at
                    checkout
                  </p>
                </div>

                <ul className="space-y-3 self-center">
                  {pricingIncludes.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm font-medium text-fg-dim">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-gradient-soft">
                        <Check className="h-3 w-3 text-brand-pink" />
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <p className="mt-6 text-center text-sm text-fg-muted">
              Buy first, sign up after. Your account takes 20 seconds to create once payment is
              done, and your download is waiting.
            </p>
          </div>
        </section>

        {/* =============================== FAQ ============================== */}
        <section id="faq" className="mt-28 scroll-mt-24">
          <div className="mx-auto max-w-3xl space-y-10">
            <div className="space-y-4 text-center">
              <h2 className="text-3xl font-bold lg:text-4xl">Questions, answered honestly</h2>
            </div>
            <div className="space-y-3">
              {faqs.map((faq) => (
                <FaqItem key={faq.q} {...faq} />
              ))}
            </div>
          </div>
        </section>

        {/* ============================ FINAL CTA =========================== */}
        <section className="mt-28">
          <div className="relative overflow-hidden rounded-brand-lg border border-white/10 bg-brand-gradient-soft p-10 text-center sm:p-16">
            <img src="/app-icon.png" alt="" className="mx-auto mb-6 h-20 w-20" />
            <h2 className="text-3xl font-bold lg:text-4xl">
              Your keyboard is optional now.
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-lg text-fg-dim">
              Join the launch. {LAUNCH_PRICE} today instead of {RETAIL_PRICE}.
            </p>
            <p className="mx-auto mt-3 max-w-xl text-sm font-semibold text-brand-pink">
              {LAUNCH_CTA_URGENCY}
            </p>
            <div className="mt-8 flex justify-center">
              <BuyButton label={LAUNCH_CTA_LABEL} />
            </div>
            <p className="mt-6 inline-flex items-center gap-2 text-xs font-medium text-fg-muted">
              <Download className="h-3.5 w-3.5" /> Instant download · macOS 14.4+ · Apple Silicon &amp; Intel
            </p>
          </div>
        </section>

        {/* ============================== FOOTER ============================ */}
        <footer className="mt-24 border-t border-white/10 py-12">
          <div className="flex flex-col items-center justify-between gap-6 sm:flex-row">
            <div className="flex items-center gap-2.5">
              <img src="/app-icon.png" alt="" className="h-7 w-7" />
              <span className="text-sm font-semibold text-fg-dim">AgentWhisper</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-fg-muted">
              <a href="#pricing" className="transition-colors hover:text-fg">Pricing</a>
              <a href="#faq" className="transition-colors hover:text-fg">FAQ</a>
              <a href={PORTAL_URL} className="transition-colors hover:text-fg">Sign in</a>
              <a href="mailto:altuzar@gmail.com" className="transition-colors hover:text-fg">Support</a>
              <a href="/privacy" className="transition-colors hover:text-fg">Privacy</a>
              <a href="/terms" className="transition-colors hover:text-fg">Terms</a>
            </div>
            <p className="text-xs text-fg-muted">
              © {new Date().getFullYear()} Fernando Altuzar. All rights reserved.
            </p>
          </div>
        </footer>
      </main>
    </div>
  )
}
